
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { getDomainFromUrl } from '@/utils/domain';
import { fetchSitemapUrls, categorizeUrls } from '@/utils/sitemap';
import { generateContent } from '@/utils/content';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnalysisProgress } from '@/components/AnalysisProgress';
import { UrlCategories, addScanToHistory } from '@/utils/analysisHelpers';

interface SearchFormProps {
  addRecentSite: (domain: string) => void;
  simulateProgress: () => () => void;
}

export const SearchForm: React.FC<SearchFormProps> = ({ 
  addRecentSite, 
  simulateProgress 
}) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [seoProgress, setSeoProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // AdSense video ad function
  const showVideoAd = () => {
    return new Promise<void>((resolve) => {
      // Create AdSense video ad container
      const adContainer = document.createElement('div');
      adContainer.className = 'fixed inset-0 bg-black/80 flex items-center justify-center z-50';
      adContainer.innerHTML = `
        <div class="bg-white rounded-lg p-4 max-w-xl w-full">
          <div class="mb-4 text-center">
            <h3 class="text-lg font-semibold">${t('ads.watchingAd')}</h3>
            <p class="text-sm text-gray-500">${t('ads.adWillComplete')} <span id="ad-countdown">30</span> ${t('ads.seconds')}</p>
          </div>
          <div id="adsense-video" class="w-full aspect-video bg-gray-200 flex items-center justify-center">
            <ins class="adsbygoogle"
              style="display:block; width:100%; height:100%;"
              data-ad-client="ca-pub-7154913530427560"
              data-ad-slot="video-ad-slot"
              data-ad-format="video"></ins>
          </div>
        </div>
      `;
      
      document.body.appendChild(adContainer);
      
      // Initialize AdSense ad
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense error:', e);
      }
      
      // Countdown timer (30 seconds)
      let countdown = 30;
      const countdownEl = document.getElementById('ad-countdown');
      
      const timer = setInterval(() => {
        countdown--;
        if (countdownEl) countdownEl.textContent = countdown.toString();
        
        if (countdown <= 0) {
          clearInterval(timer);
          document.body.removeChild(adContainer);
          setAdWatched(true);
          resolve();
        }
      }, 1000);
    });
  };

  const cancelAnalysis = () => {
    setIsLoading(false);
    setShowProgress(false);
    setSeoProgress(0);
    toast.info(t('analysis.analysisCancelled'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowProgress(true);
    setSeoProgress(10);

    try {
      const domain = getDomainFromUrl(url);
      if (!domain) {
        toast.error(t('errors.invalidUrl'));
        setIsLoading(false);
        setShowProgress(false);
        return;
      }

      addRecentSite(domain);

      // Show video ad before continuing
      if (!adWatched) {
        await showVideoAd();
      }

      setSeoProgress(30);

      const urls = await fetchSitemapUrls(domain);
      if (!urls || urls.length === 0) {
        toast.error(t('errors.sitemapNotFound'));
        
        // Manually enter sitemap URL if not found
        const manualSitemapUrl = window.prompt(t('analysis.enterSitemapUrl'), `https://${domain}/sitemap.xml`);
        
        if (manualSitemapUrl) {
          try {
            setSeoProgress(40);
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(manualSitemapUrl)}`;
            const response = await fetch(proxyUrl);
            
            if (!response.ok) {
              throw new Error(`Sitemap alınamadı: ${response.status}`);
            }
            
            const text = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(text, "text/xml");
            
            const urlElements = xmlDoc.getElementsByTagName("url");
            const manualUrls = [];
            
            for (let i = 0; i < urlElements.length; i++) {
              const loc = urlElements[i].getElementsByTagName("loc")[0];
              if (loc?.textContent) {
                manualUrls.push(loc.textContent);
              }
            }
            
            if (manualUrls.length === 0) {
              throw new Error('Manuel sitemap\'de URL bulunamadı');
            }
            
            setSeoProgress(90);
            const categories: UrlCategories = categorizeUrls(manualUrls);
            const content = generateContent(domain, categories);
            
            // Tarama geçmişine kaydet
            addScanToHistory(domain, manualUrls.length);
            
            setSeoProgress(100);
            setTimeout(() => {
              navigate(`/analysis/${domain}`);
            }, 1000);
            
            return;
          } catch (error) {
            toast.error(t('errors.manualSitemapError') + ': ' + (error as Error).message);
            setIsLoading(false);
            setShowProgress(false);
            return;
          }
        } else {
          setIsLoading(false);
          setShowProgress(false);
          return;
        }
      }

      setSeoProgress(70);
      const categories: UrlCategories = categorizeUrls(urls);
      const content = generateContent(domain, categories);
      
      // Tarama geçmişine kaydet
      addScanToHistory(domain, urls.length);
      
      setSeoProgress(100);
      setTimeout(() => {
        navigate(`/analysis/${domain}`);
      }, 1000);
    } catch (error) {
      toast.error(t('errors.generalError') + ': ' + (error as Error).message);
      setIsLoading(false);
      setShowProgress(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Input
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t('buttons.analyzing') : t('buttons.analyze')}
        </Button>
      </form>
      
      <AnalysisProgress
        seoProgress={seoProgress}
        backlinkProgress={0}
        isVisible={showProgress}
        onCancel={cancelAnalysis}
      />
    </>
  );
};
